import { AlertFactory } from 'alerts/Alert'
import ModalViewModel from 'infra/ModalViewModel'
import {
  action,
  computed,
  makeObservable,
  observable,
  override,
  runInAction,
} from 'mobx'
import { task } from 'mobx-task'
import Job from 'jobs/Job'
import TaxRefundMethodViewModel from 'jobs/info-gathering/tax-refund-method/TaxRefundMethodViewModel'
import TaxRefundMethodStore from 'jobs/info-gathering/tax-refund-method/TaxRefundMethodStore'
import InfoGatheringStore from 'jobs/InfoGatheringStore'
import TaxRefundMethodEditorViewModel from 'jobs/info-gathering/tax-refund-method/editors/TaxRefundMethodEditorViewModel'

export type RefundTab = 'personal' | 'business'

export const REFUND_TAB_LABELS: Record<RefundTab, string> = {
  personal: 'Personal',
  business: 'Business',
}

export default class EditRefundDialogViewModel extends ModalViewModel {
  readonly personalRefundEditorVm = new TaxRefundMethodEditorViewModel(
    'personal',
  )

  readonly businessRefundEditorVm = new TaxRefundMethodEditorViewModel(
    'business',
  )

  @observable activeTab: RefundTab = 'personal'

  constructor(
    private job: Job,
    private taxRefundMethodVM: TaxRefundMethodViewModel,
    private taxRefundMethodStore: TaxRefundMethodStore,
    private infoGatheringStore: InfoGatheringStore,
    private newAlert: AlertFactory,
  ) {
    super()
    makeObservable(this)
  }

  @computed
  get availableTabs(): RefundTab[] {
    const tabs: RefundTab[] = []
    if (this.taxRefundMethodVM.personalRefundMethodSupportingItem) {
      tabs.push('personal')
    }
    if (this.taxRefundMethodVM.businessRefundMethodSupportingItem) {
      tabs.push('business')
    }
    return tabs
  }

  @computed
  get hasMultipleTabs() {
    return this.availableTabs.length > 1
  }

  @computed
  get isFirstTab() {
    return this.activeTab === this.availableTabs[0]
  }

  @computed
  get isLastTab() {
    return this.activeTab === this.availableTabs[this.availableTabs.length - 1]
  }

  @computed
  get nextTab(): RefundTab | null {
    const index = this.availableTabs.indexOf(this.activeTab)
    if (index >= 0 && index < this.availableTabs.length - 1) {
      return this.availableTabs[index + 1]
    }
    return null
  }

  @computed
  get previousTab(): RefundTab | null {
    const index = this.availableTabs.indexOf(this.activeTab)
    if (index > 0) {
      return this.availableTabs[index - 1]
    }
    return null
  }

  @computed
  get activeEditor() {
    return this.activeTab === 'personal'
      ? this.personalRefundEditorVm
      : this.businessRefundEditorVm
  }

  @action.bound
  setTab(tab: RefundTab) {
    this.activeTab = tab
  }

  @action.bound
  skipCurrentTab() {
    if (this.nextTab) {
      this.activeTab = this.nextTab
      return
    }
    this.close()
  }

  @action.bound
  goToPreviousTab() {
    if (this.previousTab) {
      this.activeTab = this.previousTab
    }
  }

  @override
  show(initialTab: RefundTab = 'personal') {
    this.personalRefundEditorVm.reset(this.taxRefundMethodVM.personalRefundMethod)
    this.businessRefundEditorVm.reset(this.taxRefundMethodVM.businessRefundMethod)
    this.activeTab = this.availableTabs.includes(initialTab)
      ? initialTab
      : this.availableTabs[0] ?? 'personal'
    super.show()
  }

  submit = task.resolved(async () => {
    if (!this.activeEditor.validate()) {
      return
    }

    try {
      if (this.activeTab === 'personal') {
        await this._savePersonal()
      } else {
        await this._saveBusiness()
      }

      const nextTab = this.nextTab
      if (nextTab) {
        runInAction(() => {
          this.activeTab = nextTab
        })
      } else {
        this.close()
      }
    } catch (error) {
      this.newAlert().fromError(error).show()
      console.error(error)
    }
  })

  private async _savePersonal() {
    const editor = this.personalRefundEditorVm
    const existing = this.taxRefundMethodVM.personalRefundMethod
    const deliveryMethod = editor.toDeliveryMethodInput()

    const saved = existing?.id
      ? await this.taxRefundMethodStore.updateTaxRefundMethod({
          id: existing.id,
          deliveryMethod,
        })
      : await this.taxRefundMethodStore.createTaxRefundMethod({
          workspaceId: this.job.workspace.id,
          ownerUserId: this.job.championClient!.member.user.publicId,
          refundType: 'personal',
          deliveryMethod,
        })

    await this.infoGatheringStore.provideSupportingItemPersonalRefundMethod({
      jobId: this.job.id,
      itemId: this.taxRefundMethodVM.personalRefundMethodSupportingItem!.id,
      refundMethodId: saved.id,
      refundMethodVersion: saved.version,
    })
  }

  private async _saveBusiness() {
    const editor = this.businessRefundEditorVm
    const existing = this.taxRefundMethodVM.businessRefundMethod
    const deliveryMethod = editor.toDeliveryMethodInput()

    const saved = existing?.id
      ? await this.taxRefundMethodStore.updateTaxRefundMethod({
          id: existing.id,
          deliveryMethod,
        })
      : await this.taxRefundMethodStore.createTaxRefundMethod({
          workspaceId: this.job.workspace.id,
          ownerUserId: this.job.championClient!.member.user.publicId,
          refundType: 'business',
          deliveryMethod,
        })

    await this.infoGatheringStore.provideSupportingItemBusinessRefundMethod({
      jobId: this.job.id,
      itemId: this.taxRefundMethodVM.businessRefundMethodSupportingItem!.id,
      refundMethodId: saved.id,
      refundMethodVersion: saved.version,
    })
  }
}
